import type { MaintenanceStatus } from '@research-assistant/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, type JSX } from 'react';

import { GameContainer } from '@/features/game/GameContainer.js';

export const Route = createFileRoute('/game')({
  component: GamePage,
});

// `/game` is intentionally only reachable while maintenance is on — the
// MaintenanceScreen is the only published entry point, and once the bridge is
// back the user should be using the app, not playing. If someone deep-links
// the route outside maintenance we redirect home after the status query has
// actually resolved (otherwise the initial "{ enabled: false }" fallback
// would eject everyone before /v1/maintenance answers).
function GamePage(): JSX.Element {
  const navigate = useNavigate();

  // Same query key as useMaintenance — TanStack Query dedupes, so we read
  // the cache without firing a duplicate fetch.
  const status = useQuery<MaintenanceStatus>({
    queryKey: ['maintenance-status'],
    queryFn: async () => {
      const res = await fetch('/v1/maintenance', { credentials: 'omit' });
      if (!res.ok)
        return { enabled: false, title: '', message: '', allowAdmins: true };
      return (await res.json()) as MaintenanceStatus;
    },
    staleTime: 5_000,
  });

  const allowed = status.data?.enabled ?? null;

  useEffect(() => {
    if (allowed === false) {
      void navigate({ to: '/', replace: true });
    }
  }, [allowed, navigate]);

  if (allowed !== true) return <></>; // still loading or about to redirect

  return <GameContainer onBack={() => navigate({ to: '/' })} />;
}
